import React, {useContext, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import {
  checkGuestBookingsLoaded,
  getAvailableChannels,
  gradientRgb,
} from "utilities/helperFunctions";
import ICONS from "connectui/icons/iconMap";

import {MessagesContext} from "../../panel/conversations/ConversationsPanel";
import FlexContainer from "connectui/component/layout/FlexContainer";
import SvgIcon from "connectui/component/display/SvgIcon";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import BookmarkButtonBase from "connectui/component/input/button/base/BookmarkButtonBase";
import Divider from "connectui/component/display/Divider";
import ButtonIconText from "connectui/component/input/button/ButtonIconText";
import {useFeatureFlags} from "utilities/featureUtil";

const useStyles = (theme) =>
  buildStyles({
    row: {
      width: "100%",
      height: "30px",
      padding: theme.spacing(0, 4),
      backgroundColor: "transparent",
      position: "absolute",
      top: "-31px",
      left: 0,
    },
    icon: {
      width: "30px",
      height: "30px",
      justifyContent: "center",
    },
    noteButton: {
      padding: theme.spacing(1.5, 2, 1),
      borderRadius: theme.spacing(2, 2, 0, 0),
      gap: theme.spacing(1.5),
      display: "flex",
      alignItems: "center",
      border: "none",
      outline: "none",
      color: theme.colors.textSecondary,
      backgroundColor: gradientRgb(theme.colors.gray50, 0.7),
      backdropFilter: "blur(3px)",
      marginBottom: "1px",
      "&:hover": {
        backgroundColor: theme.colors.gray100,
      },
    },
    noteButtonSelected: {
      color: theme.colors.warning900,
      backgroundColor: theme.colors.warning200,
      borderBottom: `1px solid ${theme.colors.warning200}`,
      boxShadow: "none",
      "&:hover": {backgroundColor: theme.colors.warning200},
    },
  });

const ChannelRow = ({
  guestId,
  guest = {},
  lastMessage = {},
  whatsappAccountId,
  activeChannel,
  setActiveChannel,
  disabled,
  allChannelsDisabled,
  setAvailableChannels = () => {},
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const {lodgifyMessaging, lodgifyOta, hostawayOta} = useFeatureFlags();
  const {replyEmailProps} = useContext(MessagesContext);
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );
  const guestBookings = useSelector(
    (state) => state.defaultReducer.selected_guest_bookings,
  );
  const [isChannelSelected, setIsChannelSelected] = useState(false);

  const guestBookingsLoaded = useMemo(
    () => checkGuestBookingsLoaded(guest, guestId, guestBookings),
    [guestBookings, guestId],
  );

  // Reset channel selection flag when changing conversation
  useEffect(() => setIsChannelSelected(false), [guestId]);

  useEffect(() => {
    if (replyEmailProps.enabled) setIsChannelSelected(true);
  }, [replyEmailProps]);

  const availableChannels = useMemo(() => {
    if (!guestBookingsLoaded) return [];
    return getAvailableChannels({
      guest,
      flags: {lodgifyMessaging, lodgifyOta, hostawayOta},
      guestBookings,
      user_profile,
      whatsappData: {accountId: whatsappAccountId},
    });
  }, [
    guest,
    user_profile,
    guestBookings,
    guestBookingsLoaded,
    lodgifyMessaging,
    lodgifyOta,
    hostawayOta,
    whatsappAccountId,
  ]);

  useEffect(() => {
    if (!guestBookingsLoaded) return;
    setAvailableChannels(availableChannels);
  }, [availableChannels, guestBookingsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (disabled) return;

    const channelIsAvailable = availableChannels.some(
      (c) => c.id === activeChannel && c.show,
    );
    // User has selected a channel and it's available
    if (isChannelSelected && channelIsAvailable) return;

    let newChannel = null;
    if (
      lastMessage.channel &&
      availableChannels.some((c) => c.id === lastMessage.channel && c.show)
    ) {
      // Use the channel of the last message if available and valid
      newChannel = lastMessage.channel;
    } else {
      // Select the first available channel
      newChannel = availableChannels.find((c) => c.show)?.id || null;
    }

    if (activeChannel !== newChannel) {
      setActiveChannel(newChannel);
    }
  });

  const handleChannelClick = (channelId) => () => {
    setIsChannelSelected(true);
    setActiveChannel(channelId);
  };

  return (
    <FlexContainer alignItems="center" className={classes.row}>
      {availableChannels.map((c) => {
        // Hide unavailable channels
        if (!c.show) return null;
        const selected = activeChannel === c.id;

        return (
          <ButtonIcon
            key={c.id}
            disabled={disabled}
            buttonBase={BookmarkButtonBase}
            active={selected}
            icon={
              <SvgIcon
                component={ICONS[c.icon]}
                className={classes.icon}
                sx={{
                  padding: c.padding,
                  opacity:
                    !selected && c.id === "expedia" ? 0.4 : disabled ? 0.3 : 1,
                  color:
                    selected && !disabled
                      ? c.color
                      : theme.colors[
                          c.id === "expedia" ? "gray400" : "gray500"
                        ],
                }}
              />
            }
            onClick={handleChannelClick(c.id)}
          />
        );
      })}
      {!allChannelsDisabled && (
        <Divider
          orientation="vertical"
          sx={{height: "13px", margin: theme.spacing(0, 1)}}
        />
      )}
      <ButtonIconText
        label="Note"
        weight="medium"
        color={activeChannel === "note" ? "warning900" : "gray700"}
        typographyVariant="textSM"
        className={`${classes.noteButton} ${activeChannel === "note" ? classes.noteButtonSelected : ""}`}
        onClick={handleChannelClick("note")}
        icon={
          <SvgIcon
            component={ICONS.note}
            sx={{
              width: "20px",
              height: "20px",
              padding: theme.spacing(0.5),
            }}
          />
        }
      />
    </FlexContainer>
  );
};

export default ChannelRow;
